/*
 * VEEZE product gallery
 * =====================
 *
 * Swaps the main image when a thumbnail is picked. Deliberately small: the
 * thumbnails are real <button>s inside the markup, each already carrying the
 * full-size URL, so with no JavaScript the page still shows the featured image
 * and every product image is still in the document for crawlers and for anyone
 * with scripting off. Nothing here is required for the page to function.
 *
 * The add-to-cart form is Horizon's own <product-form-component>, so it is not
 * touched here — that is what makes the VEEZE cart drawer open on submit.
 */

class VeezeProductGallery extends HTMLElement {
  #main = null;

  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    this.#main = this.querySelector('[data-main-image]');
    if (!this.#main) return;
    this.addEventListener('click', this.#onClick);

    /*
     * The event is dispatched on the picker and caught here on the way up, so
     * the listener has to sit on a shared ancestor rather than on the gallery.
     * `.shopify-section` is that ancestor, and it is also what Horizon's own
     * components listen on — so a second product section on the page cannot
     * hear this one's picker.
     */
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener('shopify:product:select', this.#onProductSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#section?.removeEventListener('shopify:product:select', this.#onProductSelect);
  }

  #onClick = (event) => {
    const thumb = event.target.closest('[data-thumb]');
    if (!thumb) return;

    const full = thumb.dataset.full;
    if (full) {
      // srcset would otherwise keep winning over the new src.
      this.#main.removeAttribute('srcset');
      this.#main.src = full;
    }

    const inner = thumb.querySelector('img');
    if (inner) this.#main.alt = inner.alt || this.#main.alt;

    for (const other of this.querySelectorAll('[data-thumb]')) {
      other.setAttribute('aria-current', other === thumb ? 'true' : 'false');
    }
  };

  /*
   * Follow the variant picker.
   *
   * `variant-picker.js` morphs only itself and then fires
   * `shopify:product:select`, whose promise carries the whole product page it
   * fetched. Reading the new image out of THAT document rather than out of the
   * variant JSON is deliberate: the document is this section re-rendered, so
   * the `src`, the `srcset` widths and the alt text are the ones Liquid would
   * have written — no URL assembled by hand, no width guessed.
   *
   * A variant with no image of its own leaves the gallery alone, which is the
   * same thing Liquid does.
   */
  #onProductSelect = async (event) => {
    try {
      const { detail } = await event.promise;
      const doc = detail?.html;
      if (!doc || !this.#main) return;

      const fresh = doc.querySelector('[data-main-image]');
      if (!(fresh instanceof HTMLImageElement)) return;
      if (!fresh.getAttribute('src')) return;

      /* srcset would keep winning over the new src, exactly as in #onClick. */
      this.#main.removeAttribute('srcset');
      this.#main.setAttribute('src', fresh.getAttribute('src'));

      const srcset = fresh.getAttribute('srcset');
      if (srcset) this.#main.setAttribute('srcset', srcset);
      if (fresh.alt) this.#main.alt = fresh.alt;

      /* The picked variant's image may be one of the thumbnails; mark it. */
      const shown = this.#main.getAttribute('src');
      for (const thumb of this.querySelectorAll('[data-thumb]')) {
        const isShown = Boolean(thumb.dataset.full) && shown?.includes(thumb.dataset.full.split('?')[0]);
        thumb.setAttribute('aria-current', isShown ? 'true' : 'false');
      }
    } catch {
      /* An aborted fetch is the normal outcome of clicking two sizes quickly. */
    }
  };
}

if (!customElements.get('veeze-product-gallery')) {
  customElements.define('veeze-product-gallery', VeezeProductGallery);
}

/*
 * The rating beside the price, from the review app's live data.
 *
 * The app writes both the summary and the reviews into the page server-side, so
 * this needs no request and no polling — the script is deferred, so those inline
 * scripts have already run.
 *
 * Liquid has already rendered the metafield values into this row. They stay
 * exactly as they are unless live figures are found, which keeps the row honest
 * on a page without the app block. What it fixes is the disagreement: the
 * reviews section reads this same data, so the two can no longer show different
 * numbers for the same product.
 */
function veezeLiveRating() {
  const row = document.querySelector('[data-rating-live]');
  if (!row) return;

  const summary = window.$prDoranInit?.widgetProduct?.[row.dataset.productId];
  if (!summary) return;

  const total = Number.parseInt(summary.reviewsCount, 10);
  const average = Number.parseFloat(summary.averageRating);
  if (!Number.isFinite(total) || total <= 0) return;

  const locale = row.dataset.locale || 'de-DE';
  const stars = Number.parseInt(row.dataset.stars, 10) || 5;

  const format = (value, decimals) => {
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    } catch {
      return String(value);
    }
  };

  if (Number.isFinite(average)) {
    const score = row.querySelector('.veeze-product__score');
    if (score) score.textContent = format(average, 1);

    /* The row draws its stars as characters, so the filled count is rounded the
       same way Liquid rounds it. */
    const glyphs = row.querySelector('.veeze-product__stars');
    if (glyphs) {
      const filled = Math.round(average);
      glyphs.textContent = '★'.repeat(Math.min(filled, stars))
        + '☆'.repeat(Math.max(stars - filled, 0));
    }
  }

  /* Revealed here, not in Liquid: on a product whose metafield has not been
     written yet the row ships hidden, and this is the first point at which
     there is something true to put in it. */
  row.hidden = false;

  const link = row.querySelector('.veeze-product__reviews-link');
  if (!link) return;

  /* One review is a normal state for a product, so the singular is a setting
     rather than a rounding error. */
  const label = total === 1
    ? row.dataset.labelOne || row.dataset.label
    : row.dataset.label;

  link.textContent = `${format(total, 0)} ${label}`.trim();
  link.hidden = false;
}

/*
 * Buy now, on a variant change.
 *
 * `product-form-component` owns the add-to-cart form and updates its hidden
 * `id` itself. The buy-now form is deliberately OUTSIDE that component — its
 * `name="checkout"` submit has to reach the browser untouched — which also
 * means nothing updates it. Without this, picking a second size would still
 * check out with the first.
 *
 * Both values are copied from the re-rendered section rather than computed:
 * whether the button should show at all is a Liquid decision
 * (`variant.available`), and this keeps it there.
 */
document.addEventListener('shopify:product:select', async (event) => {
  const section = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
  const wrap = section?.querySelector('[data-veeze-buynow]');
  if (!wrap) return;

  try {
    const { detail } = await event.promise;
    const fresh = detail?.html?.querySelector('[data-veeze-buynow]');
    if (!fresh) return;

    const input = wrap.querySelector('[data-veeze-buynow-id]');
    const freshInput = fresh.querySelector('[data-veeze-buynow-id]');
    if (input instanceof HTMLInputElement && freshInput instanceof HTMLInputElement) {
      input.value = freshInput.value;
    }

    wrap.toggleAttribute('hidden', fresh.hasAttribute('hidden'));
  } catch {
    /* Aborted by a newer selection. */
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', veezeLiveRating, { once: true });
} else {
  veezeLiveRating();
}

/*
 * The gallery is a custom element and re-upgrades on its own whenever the
 * theme editor re-inserts the section. This function does not — it ran once,
 * against a row that has since been replaced — so the rating row would sit
 * empty until a full reload. Storefront visitors never saw it; every merchant
 * editing this section did.
 */
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element && event.target.querySelector('[data-rating-live]')) {
    veezeLiveRating();
  }
});
